import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminService } from '../admin.service';

@Component({
  selector: 'app-admin-add-promotional-notification',
  templateUrl: './admin-add-promotional-notification.component.html',
  styleUrls: ['./admin-add-promotional-notification.component.css']
})
export class AdminAddPromotionalNotificationComponent implements OnInit {
  notificationForm: FormGroup;
  token: string | null = null;
  file: File | null = null;
  imageLoading: boolean = false;
  uploadedImageUrl: string = '';
  isEditMode: boolean = false;
  editId: string = '';

  triggerTypes = [
    'New promotional campaigns',
    'Route-based offers',
    'Price drop alerts',
    'Coupon / cashback offers',
    'Re-engagement campaigns',
    'Festival / event-based promotions',
    'Feature announcements'
  ];

  constructor(
    private fb: FormBuilder,
    private adminService: AdminService,
    private matDialogRef: MatDialogRef<AdminAddPromotionalNotificationComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.notificationForm = this.fb.group({
      triggerType: ['', Validators.required], // keeping this required for the UI, though not sent if backend doesn't want it
      title: ['', Validators.required],
      body: ['', Validators.required],
      cta_text: ['', Validators.required],
      deep_link: ['', Validators.required],
      schedule_at: ['']
    });
  }

  ngOnInit(): void {
    this.token = localStorage.getItem('adminToken');
    
    if (this.data) {
      this.isEditMode = true;
      this.editId = this.data._id || this.data.id;
      this.uploadedImageUrl = this.data.image_url || '';
      
      this.notificationForm.patchValue({
        triggerType: this.triggerTypes[0], // fallback since it's not in the payload
        title: this.data.title,
        body: this.data.body,
        cta_text: this.data.cta_text,
        deep_link: this.data.deep_link,
        schedule_at: this.data.schedule_at
      });
    }
  }

  close() {
    this.matDialogRef.close(false);
  }

  onFileChange(event: any) {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        this.snackBar.open("Invalid file format. Please upload JPG, PNG, or WEBP.", "Close", {
          duration: 4000,
        });
        return;
      }

      this.file = file;
      this.imageLoading = true;
      this.adminService.uploadImage(this.file).subscribe({
        next: (response: any) => {
          this.imageLoading = false;
          if (response.status === 201 || response.status === 200) {
            this.uploadedImageUrl = response.body.image_link || response.body.url; // adjust property based on backend
            this.snackBar.open("Image uploaded successfully!", "OK", { duration: 3000 });
          }
        },
        error: (error) => {
          this.imageLoading = false;
          this.snackBar.open("Image upload failed.", "Close", { duration: 5000 });
        }
      });
    }
  }

  removeImage() {
    this.file = null;
    this.uploadedImageUrl = '';
  }

  saveNotification() {
    if (this.notificationForm.valid) {
      const payload = {
        title: this.notificationForm.value.title,
        body: this.notificationForm.value.body,
        image_url: this.uploadedImageUrl,
        cta_text: this.notificationForm.value.cta_text,
        deep_link: this.notificationForm.value.deep_link,
        target_locations: [],
        target_routes: [],
        schedule_at: this.notificationForm.value.schedule_at || new Date().toISOString(),
        is_active: false
      };

      if (this.isEditMode) {
        this.adminService.updatePromotionalPushNotification(this.editId, payload, this.token).subscribe({
          next: (res: any) => {
            if (res.status === 200) {
              this.snackBar.open("Notification updated successfully!", "OK", { duration: 3000 });
              this.matDialogRef.close(true);
            }
          },
          error: (err) => {
            console.error('Error updating notification:', err);
            this.snackBar.open("Failed to update notification.", "Close", { duration: 5000 });
          }
        });
      } else {
        this.adminService.addPromotionalPushNotification(payload, this.token).subscribe({
          next: (res: any) => {
            if (res.status === 200 || res.status === 201) {
              this.snackBar.open("Notification created successfully!", "OK", { duration: 3000 });
              this.matDialogRef.close(true);
            }
          },
          error: (err) => {
            console.error('Error creating notification:', err);
            this.snackBar.open("Failed to create notification.", "Close", { duration: 5000 });
          }
        });
      }
    } else {
      this.notificationForm.markAllAsTouched();
    }
  }
}
